import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsCard, AtsEmail, AtsPerson, AtsPlacement, fmtMoney, fmtDate, timeAgo } from './api';
import { SectionCard, StatusBadge, TimelinePanel, TasksPanel, NotesPanel } from './shared';

// The ATS person record, Spott-informed layout: main tabs on the left
// (Overview | Experience | Emails), a persistent right panel with
// Details | Activity. Experience shows EVERYTHING the mapped profile holds —
// the same depth as the project side panel (current role, history, education,
// certifications, skills, languages).
interface Profile {
  id: number; project_id: number; project_name: string;
  current_position: string | null; current_company: string | null;
  location_full: string | null; headline: string | null; summary: string | null;
  all_skills: string | null; position_history: any; education_history: any;
  education_notes: string | null; li_profile_url: string | null;
  total_experience_years: number | null; photo_url: string | null;
  open_to_work: boolean | null; follower_count: number | null;
  languages: string | null; certifications: any;
  current_role_description: string | null; current_role_skills: string | null;
  working_style: string | null; employment_type: string | null;
  profile_email: string | null; personal_email: string | null; profile_phone: string | null;
}

const TABS = ['Overview', 'Experience', 'Notes', 'Emails'] as const;

const posDate = (d: any) => (d && d.year ? new Date(d.year, (d.month || 1) - 1, 1) : null);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtPosDate = (d: any) => (d && d.year ? `${d.month ? MONTHS[d.month - 1] + ' ' : ''}${d.year}` : '?');
const monthsBetween = (a: Date, b: Date) => Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth());
const fmtDuration = (months: number) => {
  const y = Math.floor(months / 12), m = months % 12;
  if (y && m) return `${y} yr${y > 1 ? 's' : ''} ${m} mo${m > 1 ? 's' : ''}`;
  if (y) return `${y} yr${y > 1 ? 's' : ''}`;
  return `${m} mo${m !== 1 ? 's' : ''}`;
};
// Profile fields arrive in mixed shapes depending on enrichment era: comma
// strings, real arrays, arrays of {name}, or JSON-encoded strings. Coerce
// defensively — never assume.
const splitList = (v: any, cap = 50): string[] => {
  if (!v) return [];
  let arr: any[];
  if (Array.isArray(v)) arr = v;
  else if (typeof v === 'string') {
    const t = v.trim();
    if (t.startsWith('[')) { try { const p = JSON.parse(t); arr = Array.isArray(p) ? p : [t]; } catch { arr = t.split(','); } }
    else arr = t.split(',');
  } else return [];
  return arr
    .map((s) => (typeof s === 'string' ? s : s && typeof s === 'object' ? String(s.name || s.label || '') : String(s ?? '')))
    .map((s) => s.trim()).filter(Boolean).slice(0, cap);
};

const asArray = (v: any): any[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-1.5">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
    <div className="text-sm text-gray-700 break-words">{children}</div>
  </div>
);

const AtsPersonDetail: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const { token } = useAuth();
  const [person, setPerson] = useState<AtsPerson | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [cards, setCards] = useState<AtsCard[]>([]);
  const [clients, setClients] = useState<{ id: number; name: string; contact_title: string | null; is_primary: boolean }[]>([]);
  const [placements, setPlacements] = useState<AtsPlacement[]>([]);
  const [emails, setEmails] = useState<AtsEmail[]>([]);
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const [panelTab, setPanelTab] = useState<'Details' | 'Activity'>('Details');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!token || !personId) return;
    atsFetch(token, `/people/${personId}`)
      .then((d) => {
        setPerson(d.person); setProfile(d.profile); setCards(d.cards);
        setClients(d.clients); setPlacements(d.placements); setEmails(d.emails || []);
        setProjects(d.projects || []);
      })
      .catch((e) => setError(e.message));
  }, [token, personId]);

  useEffect(load, [load]);
  useEffect(() => { setTab('Overview'); setPanelTab('Details'); }, [personId]);

  const positions: any[] = useMemo(() => {
    const h = asArray(profile?.position_history);
    return [...h].sort((a, b) => {
      const ae = a?.startEndDate?.end === null ? 1 : 0, be = b?.startEndDate?.end === null ? 1 : 0;
      if (ae !== be) return be - ae;
      return (b?.startEndDate?.start?.year || 0) - (a?.startEndDate?.start?.year || 0);
    });
  }, [profile]);

  const tenure = useMemo(() => {
    const now = new Date();
    const spans = positions
      .map((p) => {
        const s = posDate(p?.startEndDate?.start);
        if (!s) return null;
        const e = p?.startEndDate?.end === null ? now : posDate(p?.startEndDate?.end);
        return e ? monthsBetween(s, e) : null;
      })
      .filter((m): m is number => m !== null);
    if (!spans.length) return null;
    const current = positions.find((p) => p?.startEndDate?.end === null);
    const currentMonths = current && posDate(current.startEndDate.start) ? monthsBetween(posDate(current.startEndDate.start)!, now) : null;
    const starts = positions.map((p) => posDate(p?.startEndDate?.start)).filter(Boolean) as Date[];
    const totalMonths = starts.length ? monthsBetween(new Date(Math.min(...starts.map((d) => d.getTime()))), now) : null;
    return {
      avg: fmtDuration(Math.round(spans.reduce((a, b) => a + b, 0) / spans.length)),
      current: currentMonths !== null ? fmtDuration(currentMonths) : '—',
      total: totalMonths !== null ? fmtDuration(totalMonths) : '—',
    };
  }, [positions]);

  const skills = useMemo(() => splitList(profile?.all_skills, 60), [profile]);
  const roleSkills = useMemo(() => splitList(profile?.current_role_skills, 30), [profile]);
  const languages = useMemo(() => splitList(profile?.languages, 12), [profile]);
  const education: any[] = useMemo(() => asArray(profile?.education_history), [profile]);
  const certifications: any[] = useMemo(() => asArray(profile?.certifications), [profile]);

  const threads = useMemo(() => {
    const map = new Map<string, AtsEmail[]>();
    for (const e of emails) {
      if (!map.has(e.conversation_id)) map.set(e.conversation_id, []);
      map.get(e.conversation_id)!.push(e);
    }
    return Array.from(map.values()).sort((a, b) => new Date(b[0].sent_at).getTime() - new Date(a[0].sent_at).getTime());
  }, [emails]);

  if (error && !person) return <p className="text-sm text-red-600">{error}</p>;
  if (!person) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  const liUrl = person.linkedin_url || profile?.li_profile_url;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {profile?.photo_url ? (
            <img src={profile.photo_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-1 ring-gray-200" />
          ) : (
            <span className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-lg font-semibold flex-shrink-0">
              {person.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
              {person.is_contact && !person.is_candidate ? (
                <Link to="/ats/contacts" className="hover:text-gray-600">Contacts</Link>
              ) : (
                <Link to="/ats/candidates" className="hover:text-gray-600">Candidates</Link>
              )}
              <span>/</span>
              <span className="text-gray-500 truncate">{person.name}</span>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900">{person.name}</h2>
              {person.is_candidate && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600">Candidate</span>}
              {person.is_contact && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-600">Contact</span>}
              {profile?.open_to_work && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700">Open to work</span>}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {[profile?.current_position || person.title, profile?.current_company || person.company, profile?.location_full || person.location].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
        </div>
        {profile && (
          <Link to={`/project/${profile.project_id}`} className="flex-shrink-0 text-[11px] bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100">
            Full profile from <span className="font-semibold">{profile.project_name}</span> ↗
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Main column */}
        <div className="lg:col-span-2">
          <div className="border-b border-gray-200 mb-5">
            <nav className="flex gap-1 -mb-px">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                >
                  {t}
                  {t === 'Experience' && positions.length > 0 && <span className="ml-1 text-[10px] text-gray-400">{positions.length}</span>}
                  {t === 'Emails' && threads.length > 0 && <span className="ml-1 text-[10px] text-gray-400">{threads.length}</span>}
                </button>
              ))}
            </nav>
          </div>

          {tab === 'Overview' && (
            <div className="space-y-6">
              {(profile?.headline || profile?.summary) && (
                <SectionCard title="Profile">
                  {profile.headline && <p className="text-sm font-medium text-gray-800 mb-2">{profile.headline}</p>}
                  {profile.summary && <p className="text-sm text-gray-600 whitespace-pre-wrap">{profile.summary.slice(0, 1200)}{profile.summary.length > 1200 ? '…' : ''}</p>}
                </SectionCard>
              )}
              <SectionCard title={`Pipelines (${cards.length})`}>
                {cards.length === 0 ? (
                  <p className="text-sm text-gray-400">Not on any role's pipeline.</p>
                ) : (
                  <ul className="divide-y divide-gray-50">
                    {cards.map((c) => (
                      <li key={c.id} className="py-2.5 flex items-center gap-3 text-sm">
                        <Link to={`/ats/roles/${c.role_id}`} className="font-medium text-gray-900 hover:text-blue-700 truncate">{c.role_title}</Link>
                        <span className="text-xs text-gray-400 truncate">{c.client_name}</span>
                        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-600">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.stage_color || '#9CA3AF' }} />
                          {c.stage_name}
                        </span>
                        <StatusBadge value={c.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
              {placements.length > 0 && (
                <SectionCard title="Placements">
                  <ul className="space-y-2">
                    {placements.map((pl) => (
                      <li key={pl.id} className="text-sm">
                        <p className="text-gray-800">{pl.role_title} <span className="text-gray-400">at {pl.client_name}</span></p>
                        <p className="text-xs text-gray-400">{fmtDate(pl.start_date)} · <span className="text-emerald-600 font-medium">{fmtMoney(pl.fee_amount, pl.currency)}</span></p>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}
              {!profile && (
                <p className="text-[11px] text-gray-400 px-1">
                  No mapped profile matched yet — the full record (experience, skills, summary) attaches automatically when this person's email or LinkedIn matches a candidate in your projects. Once the dataset is live, contacts match from the universe by name + company domain too.
                </p>
              )}
            </div>
          )}

          {tab === 'Experience' && (
            <div className="space-y-6">
              {!profile ? (
                <div className="py-12 text-center">
                  <p className="text-sm text-gray-500 mb-1">No mapped profile matched.</p>
                  <p className="text-xs text-gray-400 max-w-md mx-auto">Experience pulls in automatically when this person's email or LinkedIn matches a candidate in your projects (and, post-dataset, from the universe by name + company domain).</p>
                </div>
              ) : (
                <>
                  {(profile.headline || profile.summary) && (
                    <SectionCard title="About">
                      {profile.headline && <p className="text-sm font-medium text-gray-800 mb-2">{profile.headline}</p>}
                      {profile.summary && <p className="text-sm text-gray-600 whitespace-pre-wrap">{profile.summary.slice(0, 1500)}{profile.summary.length > 1500 ? '…' : ''}</p>}
                    </SectionCard>
                  )}

                  {tenure && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-2.5 flex flex-wrap items-center gap-x-8 gap-y-1">
                      {[['Avg tenure', tenure.avg], ['Current tenure', tenure.current], ['Total experience', tenure.total]].map(([l, v]) => (
                        <span key={l} className="text-xs text-gray-400">
                          {l}: <span className="text-sm font-semibold text-gray-800">{v}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {(profile.current_role_description || roleSkills.length > 0 || profile.working_style || profile.employment_type) && (
                    <SectionCard title="Current role">
                      <p className="text-sm font-medium text-gray-800">{[profile.current_position, profile.current_company].filter(Boolean).join(' · ')}</p>
                      <p className="text-xs text-gray-400 mb-2">{[profile.employment_type, profile.working_style].filter(Boolean).join(' · ')}</p>
                      {profile.current_role_description && <p className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{profile.current_role_description}</p>}
                      {roleSkills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {roleSkills.map((s) => <span key={s} className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs">{s}</span>)}
                        </div>
                      )}
                    </SectionCard>
                  )}

                  <SectionCard title={`Positions (${positions.length})`}>
                    {positions.length === 0 ? (
                      <p className="text-sm text-gray-400">No position history on the mapped record.</p>
                    ) : (
                      <div className="space-y-5">
                        {positions.map((p, i) => {
                          const s = posDate(p?.startEndDate?.start);
                          const isPresent = p?.startEndDate?.end === null;
                          const e = isPresent ? new Date() : posDate(p?.startEndDate?.end);
                          const dur = s && e ? fmtDuration(monthsBetween(s, e)) : null;
                          return (
                            <div key={i} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${isPresent ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                {i < positions.length - 1 && <span className="flex-1 w-px bg-gray-100 mt-1" />}
                              </div>
                              <div className="min-w-0 flex-1 pb-1">
                                <p className="text-sm font-semibold text-gray-900">{p.title || '—'}</p>
                                <p className="text-sm text-gray-600">
                                  {p.companyName || p.company || '—'}
                                  {p.contractType ? <span className="text-xs text-gray-400"> · {p.contractType}</span> : null}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {fmtPosDate(p?.startEndDate?.start)} – {isPresent ? 'Present' : fmtPosDate(p?.startEndDate?.end)}
                                  {dur ? ` · ${dur}` : ''}
                                  {p.companyLocation || p.location ? ` · ${p.companyLocation || p.location}` : ''}
                                </p>
                                {p.description && <p className="text-xs text-gray-500 mt-1.5 whitespace-pre-wrap">{String(p.description).slice(0, 600)}{String(p.description).length > 600 ? '…' : ''}</p>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </SectionCard>

                  {education.length > 0 && (
                    <SectionCard title={`Education (${education.length})`}>
                      <ul className="space-y-3">
                        {education.map((ed, i) => {
                          const sy = ed?.startEndDate?.start?.year, ey = ed?.startEndDate?.end?.year;
                          const grade = ed.grade || ed.grades || ed.grade_achieved;
                          return (
                            <li key={i}>
                              <p className="text-sm font-medium text-gray-800">{ed.schoolName || ed.school || '—'}</p>
                              <p className="text-xs text-gray-500">
                                {[ed.degreeName || ed.degree, ed.fieldOfStudy].filter(Boolean).join(' · ')}
                                {sy || ey ? ` · ${sy || '?'}–${ey || '?'}` : ''}
                                {grade ? <span className="text-emerald-700 font-medium"> · Grade: {String(grade)}</span> : null}
                              </p>
                              {ed.description && <p className="text-xs text-gray-400 mt-0.5">{String(ed.description).slice(0, 240)}</p>}
                            </li>
                          );
                        })}
                      </ul>
                      {profile.education_notes && !education.some((ed) => ed.description) && (
                        <p className="text-[11px] text-gray-400 mt-3 border-t border-gray-50 pt-2">Notes: {profile.education_notes.slice(0, 300)}</p>
                      )}
                    </SectionCard>
                  )}

                  {certifications.length > 0 && (
                    <SectionCard title={`Certifications (${certifications.length})`}>
                      <ul className="space-y-2">
                        {certifications.map((c, i) => (
                          <li key={i} className="text-sm">
                            <span className="text-gray-800">{c.name || c.title || '—'}</span>
                            {(c.organizationName || c.authority) && <span className="text-xs text-gray-400"> · {c.organizationName || c.authority}</span>}
                            {c?.issuedDate?.year || c?.startEndDate?.start?.year ? <span className="text-xs text-gray-400"> · {c?.issuedDate?.year || c?.startEndDate?.start?.year}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </SectionCard>
                  )}

                  {skills.length > 0 && (
                    <SectionCard title={`Skills (${skills.length})`}>
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map((s) => <span key={s} className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs">{s}</span>)}
                      </div>
                    </SectionCard>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'Notes' && (
            <div className="max-w-3xl">
              <NotesPanel entityType="person" entityId={person.id} />
            </div>
          )}

      {tab === 'Emails' && (
            <div>
              {threads.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-sm text-gray-500 mb-1">No synced email with {person.name}.</p>
                  <p className="text-xs text-gray-400">Conversations appear here once your connected mailbox has exchanged mail with their address.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-right">
                    <Link to="/ats/inbox" className="text-xs font-medium text-emerald-700 hover:text-emerald-800">Open Inbox ↗</Link>
                  </div>
                  {threads.map((th) => (
                    <div key={th[0].conversation_id} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-800 truncate flex-1">{th[0].subject || '(no subject)'}</p>
                        {th.some((m) => m.needs_reply) && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 uppercase">Awaiting reply</span>}
                        <span className="text-[11px] text-gray-400">{th.length} msg{th.length > 1 ? 's' : ''} · {timeAgo(th[0].sent_at)}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        <span className={th[0].direction === 'outbound' ? 'text-emerald-600 font-medium' : 'text-gray-400 font-medium'}>{th[0].direction === 'outbound' ? 'You: ' : ''}</span>
                        {th[0].preview}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel: Details | Activity (Spott-style, always present) */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm self-start lg:sticky lg:top-4">
          <div className="border-b border-gray-100 px-4">
            <nav className="flex gap-1 -mb-px">
              {(['Details', 'Activity'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setPanelTab(t)}
                  className={`px-3 py-2.5 text-xs font-semibold border-b-2 ${panelTab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                >
                  {t}
                </button>
              ))}
            </nav>
          </div>
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            {panelTab === 'Details' ? (
              <div className="divide-y divide-gray-50">
                <DetailRow label="Email">{person.email || profile?.profile_email || '—'}</DetailRow>
                {(profile?.personal_email && profile.personal_email !== person.email) && (
                  <DetailRow label="Personal email">{profile.personal_email}</DetailRow>
                )}
                <DetailRow label="Phone">{person.phone || profile?.profile_phone || '—'}</DetailRow>
                <DetailRow label="LinkedIn">
                  {liUrl ? (
                    <a href={liUrl.startsWith('http') ? liUrl : `https://${liUrl}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700">Open profile ↗</a>
                  ) : '—'}
                </DetailRow>
                <DetailRow label="Location">{profile?.location_full || person.location || '—'}</DetailRow>
                {clients.length > 0 && (
                  <DetailRow label="Contact at">
                    {clients.map((c) => (
                      <span key={c.id} className="block">
                        <Link to={`/ats/clients/${c.id}`} className="text-blue-700 hover:underline">{c.name}</Link>
                        {c.contact_title && <span className="text-xs text-gray-400"> · {c.contact_title}</span>}
                      </span>
                    ))}
                  </DetailRow>
                )}
                {projects.length > 0 && (
                  <DetailRow label="Appears in projects">
                    {projects.map((p) => (
                      <span key={p.id} className="block">
                        <Link to={`/project/${p.id}`} className="text-blue-700 hover:underline">{p.name}</Link>
                      </span>
                    ))}
                  </DetailRow>
                )}
                {languages.length > 0 && (
                  <DetailRow label="Languages">
                    <span className="flex flex-wrap gap-1 mt-0.5">
                      {languages.map((l) => <span key={l} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px]">{l}</span>)}
                    </span>
                  </DetailRow>
                )}
                {profile?.follower_count ? <DetailRow label="LinkedIn followers">{profile.follower_count.toLocaleString()}</DetailRow> : null}
                <DetailRow label="Added">{fmtDate(person.created_at)}</DetailRow>
                <div className="pt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Tasks</p>
                  <TasksPanel entityType="person" entityId={person.id} />
                </div>
              </div>
            ) : (
              <TimelinePanel entityType="person" entityId={person.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtsPersonDetail;
